import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Filter, Calendar, Target, 
  Building2, UserCheck, ShieldCheck, Sparkles, ChevronRight, Clock, CheckCircle2
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { managerAPI } from '../utils/api';

const ManagerHosts = () => {
  const [searchParams] = useSearchParams();
  const bdParam = searchParams.get('bd_id');
  const agencyParam = searchParams.get('agency_id');

  const [hosts, setHosts] = useState([]);
  const [bds, setBds] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [selectedBd, setSelectedBd] = useState(bdParam || 'all');
  const [selectedAgency, setSelectedAgency] = useState(agencyParam || 'all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [toastMsg, setToastMsg] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = {};
        if (selectedBd !== 'all') params.bd_id = selectedBd;
        if (selectedAgency !== 'all') params.agency_id = selectedAgency;

        const [hostsRes, bdRes, agRes] = await Promise.all([
          managerAPI.getHosts(params),
          managerAPI.getBDs(),
          managerAPI.getAgencies()
        ]);

        if (hostsRes.data?.success) setHosts(hostsRes.data.hosts || []);
        if (bdRes.data?.success) setBds(bdRes.data.bds || []);
        if (agRes.data?.success) setAgencies(agRes.data.agencies || []);
      } catch (err) {
        console.error("Failed to load hosts:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedBd, selectedAgency]);

  const filteredHosts = hosts.filter(h => {
    const term = search.toLowerCase();
    const fullName = `${h.first_name || ''} ${h.last_name || ''}`.toLowerCase();
    const userName = (h.user_name || '').toLowerCase();
    const xoxoId = (h.xoxo_id || '').toLowerCase();
    const agencyName = (h.agency_name || '').toLowerCase();
    const bdName = (h.bd_name || '').toLowerCase();
    return fullName.includes(term) || userName.includes(term) || xoxoId.includes(term) || agencyName.includes(term) || bdName.includes(term);
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight flex items-center gap-3">
            <Users className="text-cyan-400" size={28} />
            <span>Network Host Directory</span>
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Real-time live streaming activity and target progress for all hosts in your BD portfolio.
          </p>
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="relative sm:col-span-1">
            <Search size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search host by name, ID, or BD..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[#0b0f19]/80 border border-indigo-500/15 rounded-2xl py-4 pl-14 pr-5 text-white placeholder:text-slate-500 outline-none focus:border-indigo-500/50 text-sm"
            />
          </div>

          <div>
            <select
              value={selectedBd}
              onChange={(e) => setSelectedBd(e.target.value)}
              className="w-full bg-[#0b0f19]/80 border border-indigo-500/15 rounded-2xl py-4 px-5 text-white text-sm outline-none focus:border-indigo-500/50 cursor-pointer"
            >
              <option value="all">All BDs</option>
              {bds.map(b => (
                <option key={b.id} value={b.id}>
                  BD: {b.first_name} {b.last_name} ({b.user_id})
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={selectedAgency}
              onChange={(e) => setSelectedAgency(e.target.value)}
              className="w-full bg-[#0b0f19]/80 border border-indigo-500/15 rounded-2xl py-4 px-5 text-white text-sm outline-none focus:border-indigo-500/50 cursor-pointer"
            >
              <option value="all">All Agencies</option>
              {agencies.map(a => (
                <option key={a.id} value={a.id}>
                  {a.agency_name} ({a.agency_code})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Hosts Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
          </div>
        ) : filteredHosts.length === 0 ? (
          <div className="bg-[#0b0f19]/50 border border-indigo-500/15 rounded-3xl p-12 text-center">
            <Users size={48} className="mx-auto text-slate-600 mb-4" />
            <h3 className="text-lg font-bold text-white mb-1">No Hosts Found</h3>
            <p className="text-slate-400 text-sm max-w-md mx-auto">
              {search || selectedBd !== 'all' || selectedAgency !== 'all' 
                ? 'No hosts matched the selected criteria.' 
                : 'There are no hosts registered in your network yet.'}
            </p>
          </div>
        ) : (
          <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-[32px] overflow-hidden backdrop-blur-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5 text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-white/[0.01]">
                    <th className="py-4 px-6">Host Talent</th>
                    <th className="py-4 px-4">Level</th>
                    <th className="py-4 px-4">Agency</th>
                    <th className="py-4 px-4">Assigned BD</th>
                    <th className="py-4 px-4 text-center">Live Days</th>
                    <th className="py-4 px-6 text-right">Monthly Target</th>
                    <th className="py-4 px-6 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-sm font-medium">
                  {filteredHosts.map((host) => {
                    const currentLevel = Number(host.current_level || 0);
                    return (
                      <tr key={host.user_id || host.application_id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold text-sm overflow-hidden shrink-0">
                              {host.avatar_url ? (
                                <img src={host.avatar_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                (host.first_name || host.user_name || 'H').charAt(0).toUpperCase()
                              )}
                            </div>
                            <div>
                              <p className="font-bold text-white text-sm">
                                {host.first_name ? `${host.first_name} ${host.last_name || ''}`.trim() : (host.user_name || 'Host')}
                              </p>
                              <p className="text-xs text-slate-400 font-mono mt-0.5">
                                ID: <span className="text-cyan-400 font-semibold">{host.xoxo_id || host.user_id?.substring(0, 8)}</span>
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {/* Baseline Level: Minimum Level 1 */}
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 text-indigo-300 font-bold text-xs">
                            Lv.{currentLevel}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-1.5">
                            <Building2 size={14} className="text-purple-400 shrink-0" />
                            <span className="text-white text-xs font-bold">{host.agency_name}</span>
                          </div>
                          <p className="text-[10px] text-slate-400 font-mono mt-0.5 ml-5">
                            Code: {host.agency_code}
                          </p>
                        </td>
                        <td className="py-4 px-4">
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs">
                            <UserCheck size={13} className="text-indigo-400 shrink-0" />
                            <span className="font-bold">{host.bd_name || host.bd_code || 'Assigned BD'}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="px-2.5 py-1 rounded-lg bg-white/5 text-slate-300 font-bold text-xs">
                            {host.valid_days || 0} / 30
                          </span>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <span className="font-bold text-amber-400 font-mono text-sm">
                            {(host.monthly_target_coins || 0).toLocaleString()}
                          </span>
                          <span className="text-xs text-slate-400 ml-1">coins</span>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                            {host.status || host.host_status || 'Approved'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Toast Alert */}
        {toastMsg && (
          <div className="fixed bottom-8 right-8 z-50 bg-emerald-500 text-white px-5 py-3 rounded-2xl shadow-2xl text-xs font-bold flex items-center gap-2 animate-in slide-in-from-bottom-4">
            <CheckCircle2 size={16} />
            <span>{toastMsg}</span>
          </div>
        )}
      </div>
  );
};

export default ManagerHosts;
